'use client';

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import useAccount from '@/lib/hooks/useAccount';
import SignInDrawer from '../sign-in-drawer';
import ResetPasswordDrawer from '../reset-password-drawer';
import AccountRegistrationDrawer from '../registration-drawer';
import NewPasswordDrawer from '../new-password-drawer';
import { AuthDrawerContext } from './context';
import type { AuthDrawerMode, AuthDrawerOpenPayload } from './types';

interface AuthDrawerProviderProps {
  children: ReactNode;
}

const AuthDrawerProvider = ({ children }: AuthDrawerProviderProps) => {
  const searchParams = useSearchParams();
  // EWM-661 — `isLoading` here is useAccount's SWR `result.isLoading` (true
  // only until the initial fetch resolves), not `isValidating`. That's what
  // lets the login gate below fire exactly once per fresh mount instead of
  // on every background revalidation.
  const { isLoading, loggedIn } = useAccount();

  const [mode, setMode] = useState<AuthDrawerMode | null>(null);
  const [payload, setPayload] = useState<AuthDrawerOpenPayload>({});
  // EWM-664 — dedupe the new-password branch against its own exact query
  // string. Without this, a successful reset (which logs the customer out,
  // flipping `loggedIn`) would re-run this effect and re-open the tray over
  // the sign-in handoff, because the URL still carries
  // `?auth=new-password&token=...` until the customer navigates away.
  const handledNewPasswordParamsRef = useRef<string | null>(null);

  // EWM-661 — once a given `?auth=login` URL has been evaluated against
  // hydrated auth state, don't re-evaluate it again on a later auth
  // re-validation (e.g. SWR focus revalidation) — otherwise a drawer the
  // user already closed could pop back open.
  const handledLoginParamsRef = useRef<string | null>(null);

  // `?auth=login` deep-link entry, gated on hydrated auth state so a
  // signed-in user never sees the drawer, not even a brief flash while auth
  // state hydrates. Guests (EWM-267/EWM-535) keep the existing behaviour.
  // Kept in its own effect (rather than folded into the register/reset
  // effect below) so `isLoading`/`loggedIn` never influence those branches.
  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (authParam !== 'login') return;
    if (isLoading) return;

    const dedupeKey = searchParams.toString();
    if (handledLoginParamsRef.current === dedupeKey) return;
    // Stamped before the loggedIn check by design: a signed-in user who logs
    // out client-side without navigating away from a stale `?auth=login`
    // URL won't get the drawer either — out of scope for this ticket, which
    // covers fresh page loads only.
    handledLoginParamsRef.current = dedupeKey;

    if (loggedIn) return;

    const email = searchParams.get('email') ?? undefined;
    const sessionExpired = searchParams.get('sessionExpired') === '1';
    // EWM-826 — the /verify route redirects here with the outcome of the email
    // confirmation. Undefined rather than false when absent, so the payload stays
    // sparse and the drawer's optional props behave like the others.
    const verified = searchParams.get('verified') === '1';
    const verifyError = searchParams.get('verifyError') === '1';
    // EWM-760: this effect synchronizes drawer-open state to the external
    // `?auth=login` URL query (gated on hydrated auth state per EWM-661) — a
    // genuine external-system sync, not derived/seedable state. See
    // docs/superpowers/plans/ewm-760-disables.md for the full writeup.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- EWM-760: see above
    setMode('sign-in');
    setPayload({
      email,
      sessionExpired: sessionExpired || undefined,
      verified: verified || undefined,
      verifyError: verifyError || undefined,
    });
  }, [searchParams, isLoading, loggedIn]);

  // `?auth=register` / `?auth=reset` / `?auth=new-password` deep-link entry.
  // Deliberately untouched by EWM-661 — depends only on the URL, exactly as
  // before that ticket.
  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (!authParam || authParam === 'login') return;
    const email = searchParams.get('email') ?? undefined;
    if (authParam === 'reset') {
      // EWM-760: this effect (and its register/new-password sibling branches
      // below) syncs drawer-open state to the external
      // `?auth=reset|register|new-password` URL query — a genuine
      // external-system sync. See docs/superpowers/plans/ewm-760-disables.md.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- EWM-760: see above
      setMode('reset-password');
      setPayload({ email });
    } else if (authParam === 'register') {
      setMode('register');
      setPayload({ email });
    } else if (authParam === 'new-password') {
      // EWM-664 — unlike `login`, this ALWAYS opens, regardless of `loggedIn`:
      // a customer following a reset-password email is still there to reset
      // their password even if their session happens to still be active.
      const paramsKey = searchParams.toString();
      if (handledNewPasswordParamsRef.current === paramsKey) return;
      handledNewPasswordParamsRef.current = paramsKey;
      const token = searchParams.get('token') ?? undefined;
      setMode('new-password');
      setPayload({ token });
    }
  }, [searchParams]);

  const close = useCallback(() => {
    setMode(null);
    setPayload({});
  }, []);

  const open = useCallback((nextMode: AuthDrawerMode, nextPayload: AuthDrawerOpenPayload = {}) => {
    setMode(nextMode);
    setPayload(nextPayload);
  }, []);

  // EWM-651 Quirk 2 — if the session becomes authenticated while the sign-in
  // drawer is open (e.g. `loggedIn` resolved just after a stale `?auth=login`
  // deep-link opened it), close it. EWM-664 — exempt the post-reset handoff:
  // `resetPassword()`'s getAccount revalidation can briefly flip `loggedIn` true
  // even though the reset flow immediately logs back out, and without this the
  // guard would race-close the email-prefilled Sign In tray the reset just opened.
  useEffect(() => {
    // EWM-760: this effect reacts to `loggedIn` flipping from the
    // useAccount SWR/auth context (an external auth-state signal) to close
    // the sign-in drawer — a genuine external-system sync, not local
    // derived/seeded state. See docs/superpowers/plans/ewm-760-disables.md.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- EWM-760: see above
    if (loggedIn && mode === 'sign-in' && !payload.fromPasswordReset) close();
  }, [loggedIn, mode, payload.fromPasswordReset, close]);

  const value = useMemo(() => ({ mode, payload, open, close }), [mode, payload, open, close]);

  return (
    <AuthDrawerContext.Provider value={value}>
      {children}
      <SignInDrawer
        isOpen={mode === 'sign-in'}
        initialEmail={payload.email}
        sessionExpired={payload.sessionExpired}
        verified={payload.verified}
        verifyError={payload.verifyError}
        onClose={close}
        onForgotPassword={(typedEmail) => open('reset-password', { email: typedEmail })}
        onCreateAccount={(typedEmail) => open('register', { email: typedEmail })}
      />
      <ResetPasswordDrawer
        isOpen={mode === 'reset-password'}
        initialEmail={payload.email}
        onClose={close}
        onBackToSignIn={(typedEmail) => open('sign-in', { email: typedEmail })}
      />
      <AccountRegistrationDrawer
        isOpen={mode === 'register'}
        initialEmail={payload.email}
        onClose={close}
        onBackToSignIn={(typedEmail) => open('sign-in', { email: typedEmail })}
      />
      <NewPasswordDrawer
        isOpen={mode === 'new-password'}
        token={payload.token}
        onClose={close}
        onResetSuccess={(email) => open('sign-in', { email, fromPasswordReset: true })}
      />
    </AuthDrawerContext.Provider>
  );
};

export default AuthDrawerProvider;
